import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "../styles/aviator.css";
import {
  placeAviatorBet,
  cashoutAviatorBet,
  getAviatorAutoBet,
  saveAviatorAutoBet,
  disableAviatorAutoBet,
  autoBetHeartbeat,
  getCurrentRoundWithBets,
} from "../services/api/gameApi";
import { useAuthContext } from "../context/AuthContext";
import useGame from "../hooks/useGame";
import { showSuccess, showError } from "../utils/toast";
import aviatorGif from "../assets/images/aviator.gif";
import aviatorTheme from "../assets/sounds/bg_music.mp3";
// import aviatorTheme from "../assets/sounds/aviator_theme.wav";
import aviatorSound from "../utils/aviatorSound";
import aviatorPartner from "../assets/images/aviator-partners.png";
import bgoverlay from "../assets/images/bigsunrays.svg";

// Auto Cash Out: default + minimum + maximum value (change here to adjust)
const AUTO_CASHOUT_MIN = 2;
const AUTO_CASHOUT_MAX = 10;
const AUTO_CASHOUT_DEFAULT = AUTO_CASHOUT_MIN.toFixed(2);
const clampAutoCashOut = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return AUTO_CASHOUT_DEFAULT;
  return Math.min(AUTO_CASHOUT_MAX, Math.max(AUTO_CASHOUT_MIN, n)).toFixed(2);
};

function demoPlayers(roundKey) {
  let seed = String(roundKey || "preview")
    .split("")
    .reduce((n, c) => (n * 31 + c.charCodeAt(0)) >>> 0, 2166136261);
  const next = () => {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    return seed / 4294967296;
  };

  return Array.from({ length: 45 }, (_, index) => {
    const amount = [10, 20, 50, 100, 200, 500, 1000][Math.floor(next() * 7)];
    const won = next() < 0.18;
    const x = won ? Math.round((1.1 + next() * 5.8) * 100) / 100 : 0;
    return {
      name: `d***${Math.floor(next() * 10)}`,
      amount,
      multiplier: x,
      win: Math.round(amount * x * 100) / 100,
      avatar: ["✈", "★", "◆", "●", "♣"][index % 5],
    };
  });
}

const num = (v, fallback = 0) => {
  if (v === null || v === undefined || v === "") return fallback;

  if (typeof v === "object")
    return num(v.amount ?? v.value ?? v.total, fallback);

  const n = Number(v);

  return Number.isFinite(n) ? n : fallback;
};

const money = (v) =>
  Number(v || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function normalizePlayer(p, index) {
  if (Array.isArray(p))
    return {
      name: String(p[0]),
      amount: num(p[1]),
      multiplier: num(p[2]),
      win: num(p[3]),
    };

  const amount = num(p?.amount ?? p?.betAmount ?? p?.stake ?? p?.bet);

  const multiplier = num(
    p?.cashoutMultiplier ?? p?.multiplier ?? p?.exitMultiplier,
  );

  const win = num(
    p?.winAmount ?? p?.winningAmount ?? p?.payout ?? p?.returnAmount,
    multiplier ? amount * multiplier : 0,
  );

  return {
    name: String(
      p?.playerName ??
        p?.username ??
        p?.mobile ??
        p?.user?.mobile ??
        `d***${index}`,
    ),
    amount,
    multiplier,
    win,
    avatar: p?.avatar,
  };
}

// ==========================================================
// Display multiplier: server ka multiplier jump karke aata hai,
// ye screen par 1.01, 1.02, 1.03... 0.01 ke step me chadhta hai.
// Sirf display ke liye. Real cashout/crash server se hi hote hain.
// ==========================================================
function useSteppedMultiplier(target, flying) {
  const [shown, setShown] = useState(1);
  const shownRef = useRef(100); // cents (1.00x = 100)
  const accRef = useRef(0); // fractional cents
  const sample = useRef({ value: 100, time: 0, rate: 10 }); // server value + growth (cents/s)
  const targetRef = useRef(100);
  targetRef.current = Math.round(target * 100);

  // Not flying (betting / crash): show the real value directly
  useEffect(() => {
    if (flying) return;
    shownRef.current = targetRef.current;
    accRef.current = 0;
    sample.current = { value: targetRef.current, time: 0, rate: 10 };
    setShown(targetRef.current / 100);
  }, [flying, target]);

  // Learn how fast the server multiplier really grows (cents per second)
  useEffect(() => {
    if (!flying) return;
    const now = performance.now();
    const prev = sample.current;
    const value = targetRef.current;
    let rate = prev.rate;
    if (prev.time && value > prev.value && now - prev.time > 30) {
      const inst = ((value - prev.value) / (now - prev.time)) * 1000;
      rate = prev.rate * 0.7 + inst * 0.3;
    }
    sample.current = {
      value,
      time: now,
      rate: Math.min(300, Math.max(5, rate)),
    };
  }, [flying, target]);

  // Every frame: climb exactly 1.01 -> 1.02 -> 1.03 ... one cent at a time
  useEffect(() => {
    if (!flying) return undefined;
    const LEAD = 6; // max cents shown ahead of the last server value
    let frame;
    let last = performance.now();

    const tick = (now) => {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;

      const t = targetRef.current;
      let cur = shownRef.current;

      if (cur > t + LEAD) {
        cur = t;
        accRef.current = 0;
      } else {
        const gap = t - cur;
        let speed = sample.current.rate;
        if (gap > 0)
          speed = Math.max(speed, gap * 3); // catch up smoothly
        else if (cur >= t + LEAD) speed = 0;
        accRef.current += speed * dt;
        const steps = Math.floor(accRef.current);
        if (steps > 0) {
          accRef.current -= steps;
          cur = Math.min(cur + steps, Math.max(t + LEAD, cur + 1));
        }
      }

      if (cur !== shownRef.current) {
        shownRef.current = Math.max(100, cur);
        setShown(shownRef.current / 100);
      }
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [flying]);

  return shown;
}

function Plane() {
  return (
    <div className="aviator-plane" aria-hidden="true">
      <img src={aviatorGif} className="aviator-plane-icon" alt="" />

      <div className="aviator-flame" />
    </div>
  );
}

function BetCard({
  amount,
  setAmount,
  onBet,
  disabled,
  label,
  auto,
  active,
  locked,
  multiplier,
  betAmount,
  betAutoCashOut = null, // live bet par lagi auto cash out value (agar hai)
  onCashout,
  minBet = 10.0,
  maxBet = 10000,
  autoBetEnabled = false,
  setAutoBetEnabled = () => {},
  autoCashOutEnabled = false,
  setAutoCashOutEnabled = () => {},
  autoCashOut = "1.10",
  setAutoCashOut = () => {},
  cashOutAutoEnabled = false,
  manualBlocked = false,
}) {
  const cashout = Number(betAmount || 0) * Number(multiplier || 1);

  // Auto Cash Out tabhi active jab Auto bet + Auto Cash Out dono ON hon
  const autoCashOutActive = autoBetEnabled && autoCashOutEnabled;

  // Chal rahi bet par auto cash out laga hai => manual Cash Out disabled.
  // Auto Cash Out OFF => manual Cash Out enabled.
  const cashoutLockedByAuto =
    active && cashOutAutoEnabled && Number(betAutoCashOut) > 0;

  const setSafe = (value) => {
    const numeric = Number(value);

    if (!Number.isFinite(numeric)) return;

    setAmount(Math.min(maxBet, Math.max(minBet, Math.round(numeric))));
  };

  const handleAmountChange = (event) => {
    const raw = event.target.value;

    if (raw === "") {
      setAmount("");

      return;
    }

    const numeric = Number(raw);

    if (!Number.isFinite(numeric)) return;

    setAmount(Math.min(maxBet, Math.max(minBet, Math.round(numeric))));
  };

  const quick = [10, 50, 100, 500];
  const [activeTab, setActiveTab] = useState("bet");

  // Auto bet ON => AUTO bet (auto cash out ke saath agar ON ho),
  // Auto bet OFF => MANUAL bet
  const handleBetClick = () =>
    onBet(
      amount,
      auto || autoBetEnabled ? "AUTO" : "MANUAL",
      autoCashOutActive ? Number(autoCashOut) : null,
    );

  const amountBlock = (
    <div>
      <div className="aviator-amount">
        <button
          type="button"
          disabled={disabled || amount <= minBet}
          onClick={() => setSafe(amount - 10)}
        >
          −
        </button>

        <div className="aviator-amount-editor">
          <label>
            <input
              type="number"
              min={minBet}
              max={maxBet}
              step="1"
              inputMode="numeric"
              value={amount}
              onChange={handleAmountChange}
              onBlur={() => setSafe(amount || minBet)}
              disabled={disabled}
              aria-label="Bet amount"
            />
          </label>
        </div>

        <button
          type="button"
          disabled={disabled || amount >= maxBet}
          onClick={() => setSafe(amount + 10)}
        >
          +
        </button>
      </div>

      <div className="aviator-quick">
        {quick.map((v) => (
          <button
            key={v}
            type="button"
            className={amount === v ? "selected" : ""}
            disabled={disabled}
            onClick={() => setSafe(v)}
          >
            {v.toLocaleString("en-IN")}
          </button>
        ))}
      </div>
    </div>
  );

  // Dono tabs (Bet / Auto) me same action button: Cash Out / Waiting / Bet
  const betAction = (
    <div className="aviator-bet-action">
      {active ? (
        <button
          type="button"
          className="aviator-bet-button cashout"
          disabled={cashoutLockedByAuto}
          onClick={onCashout}
        >
          <span>
            {cashoutLockedByAuto
              ? `Auto @ ${Number(betAutoCashOut).toFixed(2)}x`
              : "Cash Out"}
          </span>
          <b>₹{money(cashout)}</b>
        </button>
      ) : locked ? (
        <button type="button" className="aviator-bet-button waiting" disabled>
          <span>BET PLACED</span>
          <b>WAITING FOR NEXT ROUND</b>
        </button>
      ) : (
        <button
          type="button"
          className="aviator-bet-button"
          disabled={disabled}
          onClick={handleBetClick}
        >
          <span>
            {disabled
              ? label || "BETTING CLOSED"
              : manualBlocked
                ? "AUTO BET ON"
                : "Bet"}
          </span>
          <b>₹{money(amount)}</b>
        </button>
      )}
    </div>
  );

  return (
    <section className="aviator-bet_card">
      {/* --- TOP TABS (Bet / Auto) --- */}
      <div className="aviator-tabs_game">
        <button
          type="button"
          className={activeTab === "bet" ? "active" : ""}
          onClick={() => setActiveTab("bet")}
        >
          Bet
        </button>
        {/* <button
          type="button"
          className={activeTab === "auto" ? "active" : ""}
          onClick={() => setActiveTab("auto")}
        >
          Auto
        </button> */}
      </div>

      {/* --- MAIN CONTENT AREA --- */}
      <div className="aviator-tab-content">
        {/* ================= BET TAB ================= */}
        {activeTab === "bet" && (
          <>
            {(auto || autoBetEnabled) && (
              <span className="aviator-auto">AUTO BET</span>
            )}

            {amountBlock}

            {betAction}
          </>
        )}

        {/* ================= AUTO TAB ================= */}
        {activeTab === "auto" && (
          <div className="aviator-auto-section">
            <div className="d-flex gap-2">
              {amountBlock}

              {betAction}
            </div>

            {/* Bottom Auto Settings Row */}
            {/* <div className="aviator-auto-settings">
              <div className="setting-item">
                <span>Auto bet</span>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={autoBetEnabled}
                    onChange={(e) => setAutoBetEnabled(e.target.checked)}
                  />
                  <span className="slider round"></span>
                </label>
              </div>

              <div className="setting-item">
                <span>Auto Cash Out</span>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={autoCashOutEnabled}
                    disabled={disabled || !autoBetEnabled}
                    onChange={(e) => setAutoCashOutEnabled(e.target.checked)}
                  />
                  <span className="slider round"></span>
                </label>
              </div>

              <div className="setting-input">
                <input
                  type="number"
                  min={AUTO_CASHOUT_MIN}
                  max={AUTO_CASHOUT_MAX}
                  step="0.01"
                  inputMode="decimal"
                  value={autoCashOut}
                  disabled={disabled || !autoCashOutEnabled}
                  onChange={(e) => setAutoCashOut(e.target.value)}
                  onBlur={() => setAutoCashOut(clampAutoCashOut(autoCashOut))}
                />
                <button
                  type="button"
                  className="close-btn"
                  onClick={() => setAutoCashOutEnabled(false)}
                  aria-label="Disable auto cash out"
                >
                  ×
                </button>
              </div>
            </div> */}
          </div>
        )}
      </div>
    </section>
  );
}

export default function Game({ navigate }) {
  const game = useGame();
  const boardPhase = String(
    game.boardPhase || game.phase || "BETTING",
  ).toUpperCase();

  const { wallet, updateWallet } = useAuthContext();

  const [myBet1, setMyBet1] = useState(null);

  const [myBet2, setMyBet2] = useState(null);

  const MIN_BET = 10;

  const MAX_BET = 10000;

  const [amount1, setAmount1] = useState(MIN_BET);

  const [amount2, setAmount2] = useState(MIN_BET);

  const [autoBet1, setAutoBet1] = useState(false);
  const [autoBet2, setAutoBet2] = useState(false);
  const [autoCashOut1, setAutoCashOut1] = useState(false);
  const [autoCashOut2, setAutoCashOut2] = useState(false);
  const [autoCashOutMultiplier1, setAutoCashOutMultiplier1] =
    useState(AUTO_CASHOUT_DEFAULT);
  const [autoCashOutMultiplier2, setAutoCashOutMultiplier2] =
    useState(AUTO_CASHOUT_DEFAULT);
  const [autoLoaded, setAutoLoaded] = useState(false);
  const autoActive = autoBet1 || autoBet2;

  const [tab, setTab] = useState("All Bets");

  const [menuOpen, setMenuOpen] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const themeAudio = useRef(null);
  const menuRef = useRef(null);

  const [showHistory, setShowHistory] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 576);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 576);
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const closeOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target))
        setMenuOpen(false);
    };
    const closeEscape = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeEscape);
    };
  }, [menuOpen]);

  const [error, setError] = useState("");

  const [cashoutNotice, setCashoutNotice] = useState(null);

  // ==========================================================
  // NOTICE: ek hi timer. Naya message purane ka timer cancel
  // kar deta hai, isliye auto bet / auto cash out / manual
  // cash out ke messages ek dusre ko gayab nahi karte.
  // ==========================================================
  const noticeTimer = useRef(null);

  const showNotice = (notice, ms = 4000) => {
    window.clearTimeout(noticeTimer.current);
    setCashoutNotice(notice);
    noticeTimer.current = window.setTimeout(() => setCashoutNotice(null), ms);
  };

  useEffect(() => () => window.clearTimeout(noticeTimer.current), []);

  // ==========================================================
  // AUTO BET: page load par backend se saved settings lao
  // (backend me ek user ki ek hi auto preference hoti hai => card 1)
  // ==========================================================
  useEffect(() => {
    let alive = true;

    getAviatorAutoBet()
      .then((r) => {
        if (!alive) return;

        const d = r?.data?.data ?? r?.data ?? {};

        if (d.autoBetEnabled) {
          setAutoBet1(true);
          setAutoCashOut1(!!d.autoCashOutEnabled);

          if (d.amount) {
            setAmount1(
              Math.min(
                MAX_BET,
                Math.max(MIN_BET, Math.round(Number(d.amount))),
              ),
            );
          }

          if (d.autoCashOut) {
            setAutoCashOutMultiplier1(clampAutoCashOut(d.autoCashOut));
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setAutoLoaded(true);
      });

    return () => {
      alive = false;
    };
  }, []);

  // ==========================================================
  // AUTO BET: toggle / amount / auto cash out value badalne par
  // backend preference sync karo (debounced). Auto OFF hone par
  // yeh effect kuch nahi karta; OFF handleAutoBetToggle se jaata hai.
  // ==========================================================
  useEffect(() => {
    if (!autoLoaded) return undefined;

    const slot = autoBet1 ? 1 : autoBet2 ? 2 : null;
    if (!slot) return undefined;

    const amt = Number(slot === 1 ? amount1 : amount2);
    if (!Number.isFinite(amt) || amt < MIN_BET || amt > MAX_BET)
      return undefined;

    const coOn = slot === 1 ? autoCashOut1 : autoCashOut2;
    const mult = Number(
      slot === 1 ? autoCashOutMultiplier1 : autoCashOutMultiplier2,
    );

    const timer = window.setTimeout(() => {
      saveAviatorAutoBet({
        enabled: true,
        amount: amt,
        autoCashOut: coOn ? Number(clampAutoCashOut(mult)) : null,
      }).catch((e) => {
        showError(e?.response?.data?.message || "Unable to save auto bet");
      });
    }, 500);

    return () => window.clearTimeout(timer);
  }, [
    autoLoaded,
    autoBet1,
    autoBet2,
    amount1,
    amount2,
    autoCashOut1,
    autoCashOut2,
    autoCashOutMultiplier1,
    autoCashOutMultiplier2,
  ]);

  // Auto bet toggle:
  //   ON  => doosre card ka auto band (backend me ek hi preference hai)
  //   OFF => manual mode + auto cash out OFF + backend preference OFF
  const handleAutoBetToggle = (slot) => async (enabled) => {
    const setSelf = slot === 2 ? setAutoBet2 : setAutoBet1;
    const setSelfCashOut = slot === 2 ? setAutoCashOut2 : setAutoCashOut1;
    const setSelfMultiplier =
      slot === 2 ? setAutoCashOutMultiplier2 : setAutoCashOutMultiplier1;
    const setOther = slot === 2 ? setAutoBet1 : setAutoBet2;
    const setOtherCashOut = slot === 2 ? setAutoCashOut1 : setAutoCashOut2;

    setSelf(enabled);

    if (enabled) {
      setOther(false);
      setOtherCashOut(false);
      // Auto Bet ON => Auto Cash Out also ON automatically (minimum 2.05x)
      setSelfCashOut(true);
      setSelfMultiplier((v) => clampAutoCashOut(v));
      showSuccess(
        `Auto Bet ON. Auto Cash Out ON (${AUTO_CASHOUT_MIN}x - ${AUTO_CASHOUT_MAX}x). Manual bets are disabled.`,
      );
      return;
    }

    setSelfCashOut(false);

    try {
      await disableAviatorAutoBet();
      showSuccess("Auto Bet OFF. Manual bets are enabled now.");
    } catch (e) {
      showError(e?.response?.data?.message || "Unable to disable auto bet");
    }
  };

  // Auto-bet heartbeat: tells the backend this player is still online while Auto is ON.
  useEffect(() => {
    if (!autoActive) return undefined;
    const beat = () => {
      if (!document.hidden) autoBetHeartbeat().catch(() => {});
    };
    beat();
    const id = window.setInterval(beat, 10000);
    return () => window.clearInterval(id);
  }, [autoActive]);

  // ----------------------------------------------------------
  // LIVE BET SYNC
  // ----------------------------------------------------------
  // Do NOT clear myBet merely because the socket briefly sends null during
  // a round transition. The actual bet must remain available for manual
  // Cash Out while the plane is flying.
  useEffect(() => {
    if (!game.myBet?._id) return;

    setMyBet1(game.myBet);
  }, [game.myBet]);

  // When a genuinely new round starts, clear the previous round's local bet.
  // This leaves the card ready for the backend AUTO bet to arrive.
  // Compare ids safely (id can be a string, number or populated object).
  const idOf = (v) =>
    v && typeof v === "object" ? String(v._id ?? v.id ?? "") : String(v ?? "");

  // Clear the old bet ONLY when a new betting round has really started.
  // Never clear while the plane is flying / crashed: that removed the Cash Out button.
  useEffect(() => {
    if (game.phase !== "BETTING" || !game.roundId) return;

    const current = idOf(game.roundId);
    const stale = (bet) =>
      bet && idOf(bet.roundId) && idOf(bet.roundId) !== current;

    if (stale(myBet1)) setMyBet1(null);
    if (stale(myBet2)) setMyBet2(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.phase, game.roundId]);

  // ----------------------------------------------------------
  // AUTO BET MESSAGE
  // ----------------------------------------------------------
  // Backend AUTO betting creates the real bet in the NEXT round. When the
  // socket sends that bet, show a clear message to the user.
  const lastAutoBetNoticeId = useRef(null);

  useEffect(() => {
    const bet = game.myBet;
    if (!bet?._id) return;

    const type = String(bet.betType || bet.type || "").toUpperCase();
    // betType na aaye tab bhi: Auto ON hai aur bet MANUAL nahi => auto bet hai
    const isAuto = type === "AUTO" || (type !== "MANUAL" && autoActive);
    if (!isAuto) return;

    const betId = String(bet._id);
    if (lastAutoBetNoticeId.current === betId) return;
    lastAutoBetNoticeId.current = betId;

    const amount = num(bet.amount ?? bet.betAmount ?? bet.stake, 0);
    const round = bet.roundNumber ?? bet.roundId ?? game.roundId;

    showNotice({ type: "autoBet", amount, roundId: round });
    showSuccess(`Auto Bet placed • ₹${money(amount)}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.myBet, game.roundId, autoActive]);

  // ----------------------------------------------------------
  // AUTO CASH OUT MESSAGE
  // ----------------------------------------------------------
  const lastAutoCashoutNotice = useRef(null);

  useEffect(() => {
    const bet = game.myBet;
    if (!bet?._id) return;

    const status = String(bet.status || "").toUpperCase();
    const cashedOut =
      Boolean(bet.cashedOut) ||
      ["CASHED_OUT", "CASHOUT", "WON"].includes(status);
    if (!cashedOut || lastAutoCashoutNotice.current === String(bet._id)) return;

    // Manual cash out me cashout() pehle hi id set kar deta hai, to wo yahan skip hoga
    lastAutoCashoutNotice.current = String(bet._id);

    const target = num(
      bet.cashoutMultiplier ??
        bet.multiplier ??
        bet.exitMultiplier ??
        bet.autoCashOut,
      0,
    );
    if (target <= 0) return;

    const win = num(bet.winAmount ?? bet.winningAmount ?? bet.payout, 0);
    const amountWon = win || num(bet.amount ?? bet.betAmount, 0) * target;

    showNotice({ type: "autoCashout", multiplier: target, amount: amountWon });
    showSuccess(`Auto Cash Out! ${target.toFixed(2)}x • ₹${money(amountWon)}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.myBet]);

  useEffect(() => {
    if (game.wallet && typeof game.wallet === "object")
      updateWallet(game.wallet);
  }, [game.wallet, updateWallet]);

  const rawPhase = game?.phase ?? game?.status ?? "BETTING";

  const phase = String(
    typeof rawPhase === "object"
      ? rawPhase.status || rawPhase.phase || "BETTING"
      : rawPhase,
  ).toUpperCase();

  const multiplier = Math.max(1, num(game?.multiplier, 1));

  const players = useMemo(() => {
    const source =
      Array.isArray(game?.players) && game.players.length
        ? game.players
        : demoPlayers(game?.roundId || game?.roundNumber);

    return source.map(normalizePlayer);
  }, [game?.players, game?.roundId, game?.roundNumber]);

  const visiblePlayers = useMemo(() => {
    if (tab === "Top") return [...players].sort((a, b) => b.win - a.win);

    if (tab === "Previous") return players.filter((p) => p.multiplier > 0);

    return players;
  }, [players, tab]);

  const totalWin = visiblePlayers.reduce((s, p) => s + p.win, 0);

  const betting = phase === "BETTING";

  const clampBet = (value) =>
    Math.min(MAX_BET, Math.max(MIN_BET, Math.round(Number(value) || MIN_BET)));

  const flying = phase === "FLYING";

  // Screen par dikhne wala smooth multiplier (1.01, 1.02, 1.03...)
  const displayMultiplier = useSteppedMultiplier(multiplier, flying);

  useEffect(() => {
    const music = new Audio(aviatorTheme);
    music.loop = true;
    music.volume = 0.14;
    themeAudio.current = music;
    aviatorSound.attachUnlock();
    const unlockMusic = () => {
      if (aviatorSound.enabled) music.play().catch(() => {});
      window.removeEventListener("pointerdown", unlockMusic);
      window.removeEventListener("keydown", unlockMusic);
    };
    window.addEventListener("pointerdown", unlockMusic);
    window.addEventListener("keydown", unlockMusic);
    return () => {
      window.removeEventListener("pointerdown", unlockMusic);
      window.removeEventListener("keydown", unlockMusic);
      music.pause();
      themeAudio.current = null;
      aviatorSound.stopAll();
    };
  }, []);

  useEffect(() => {
    aviatorSound.enabled = soundOn;
    if (!soundOn) {
      themeAudio.current?.pause();
      aviatorSound.stopFlight();
    } else if (aviatorSound.unlocked) {
      themeAudio.current?.play().catch(() => {});
    }
  }, [soundOn]);

  const previousSoundPhase = useRef(null);
  useEffect(() => {
    const old = previousSoundPhase.current;
    previousSoundPhase.current = phase;
    if (!soundOn || old === null || old === phase) return;
    if (phase === "FLYING") {
      aviatorSound.playRoundStart();
      aviatorSound.startFlight();
    } else if (phase === "CRASHED") {
      aviatorSound.playCrash();
    } else {
      aviatorSound.stopFlight();
    }
  }, [phase, soundOn]);

  useEffect(() => {
    if (flying && soundOn) aviatorSound.updateFlight(multiplier);
  }, [flying, multiplier, soundOn]);

  const [smoothFlightSeconds, setSmoothFlightSeconds] = useState(0);
  const flightClock = useRef({ elapsed: 0, timestamp: 0, roundId: null });
  const reportedFlightSeconds = Math.max(0, num(game?.flightElapsed));

  useEffect(() => {
    if (!flying) {
      flightClock.current = {
        elapsed: 0,
        timestamp: 0,
        roundId: game?.roundId,
      };
      setSmoothFlightSeconds(0);
      return;
    }

    const now = performance.now();
    const clock = flightClock.current;
    if (clock.roundId !== game?.roundId || !clock.timestamp) {
      // A delayed server timestamp can already be several seconds old at
      // 1.00x. Start the visual takeoff at the origin in that case.
      const initialElapsed =
        multiplier > 1.1 && reportedFlightSeconds > 1
          ? reportedFlightSeconds
          : 0;
      flightClock.current = {
        elapsed: initialElapsed,
        timestamp: now,
        roundId: game?.roundId,
      };
      setSmoothFlightSeconds(initialElapsed);
      return;
    }

    // Once started, keep one monotonic visual clock. The server clock is
    // sampled only at takeoff; subsequent 100ms updates must not move the
    // aircraft backward or change its speed between animation frames.
  }, [flying, game?.roundId, reportedFlightSeconds, multiplier]);

  useEffect(() => {
    if (!flying) return undefined;

    let frame;
    const update = (now) => {
      const clock = flightClock.current;
      setSmoothFlightSeconds(clock.elapsed + (now - clock.timestamp) / 1000);
      frame = requestAnimationFrame(update);
    };
    frame = requestAnimationFrame(update);
    return () => cancelAnimationFrame(frame);
  }, [flying]);

  const activeBet1 =
    !!myBet1?._id &&
    !myBet1.cashedOut &&
    !["WON", "CASHED_OUT", "CASHOUT"].includes(
      String(myBet1.status || "").toUpperCase(),
    );

  const activeBet2 =
    !!myBet2 &&
    !myBet2.cashedOut &&
    !["WON", "CASHED_OUT", "CASHOUT"].includes(
      String(myBet2.status || "").toUpperCase(),
    );

  // Only one bet is allowed for a user in a round. Once either bet slot has
  // a bet, both panels are locked until the next round. The actual bet slot
  // changes to Cash Out while the plane is flying.
  const hasRoundBet = !!myBet1 || !!myBet2;

  const lockedBet1 = hasRoundBet && !activeBet1;

  const lockedBet2 = hasRoundBet && !activeBet2;

  const lastCrashNoticeRound = useRef(null);

  useEffect(() => {
    if (
      phase !== "CRASHED" ||
      !game.roundId ||
      lastCrashNoticeRound.current === game.roundId
    )
      return;

    lastCrashNoticeRound.current = game.roundId;

    const losingBets = [myBet1, myBet2].filter((bet) => {
      if (!bet) return false;

      const status = String(bet.status || "").toUpperCase();

      return (
        !bet.cashedOut && !["WON", "CASHED_OUT", "CASHOUT"].includes(status)
      );
    });

    if (!losingBets.length) return;

    const lostAmount = losingBets.reduce(
      (sum, bet) => sum + num(bet.amount ?? bet.betAmount ?? bet.stake),
      0,
    );

    const crashMultiplier = num(game.crashPoint ?? game.multiplier, multiplier);

    showError(
      `Round crashed at ${crashMultiplier.toFixed(2)}x • Lost ₹${money(lostAmount)}`,
    );
  }, [
    phase,
    game.roundId,
    game.crashPoint,
    game.multiplier,
    myBet1,
    myBet2,
    multiplier,
  ]);

  // One coordinate system drives BOTH the red graph and the plane.
  // This prevents the aircraft from drifting away from the curve on different
  // screen sizes and keeps the endpoint visually locked to the graph.
  const flightSeconds = smoothFlightSeconds;

  // Reference behaviour: the plane climbs along a curve to the upper right in ~5s,
  // then floats there with a slow, small, continuous wave (never drops to the floor).
  const smoothstep = (t) => {
    const x = Math.max(0, Math.min(1, t));
    return x * x * (3 - 2 * x);
  };
  const flightProgress = flying ? 1 - Math.exp(-flightSeconds / 1.9) : 0;
  const graphX = 4 + flightProgress * 89;
  const climb = smoothstep(flightSeconds / 5.2);
  const floatBlend = smoothstep((flightSeconds - 4) / 3);
  const floatWave = Math.sin(((flightSeconds - 4) * 2 * Math.PI) / 9);
  const baseY = 96 - 76 * climb; // 96 (bottom) -> 20 (top)
  const arcPhase = ((flightSeconds - 4) * 2 * Math.PI) / 9;
  const graphY = Math.max(12, Math.min(96, baseY + 9 * floatWave * floatBlend));

  const graphRise = 96 - graphY;

  const control1X = 4 + flightProgress * 30;

  const control2X = Math.max(control1X + 8, graphX - flightProgress * 22);

  const control2Y = Math.min(96, graphY + graphRise * 0.28);

  const graphPath = `M 4 96 C ${control1X} 96, ${control2X} ${control2Y}, ${graphX} ${graphY}`;

  // The plane artwork's visible body sits inside a transparent GIF canvas.
  // Offset its box so the tail stays attached to the graph endpoint.
  // The graph group is shifted left/up on mobile. The shift grows with the flight
  // (0 at takeoff), and the plane uses the SAME shift, so the tail always stays
  // attached to the end of the red line (no gap when the plane starts flying).
  const flightShiftX = isMobile ? -9 * flightProgress : -3.8;
  const flightShiftY = isMobile ? -3 * flightProgress : 0;
  const planeX = Math.max(isMobile ? 2 : 4, graphX + flightShiftX - 1.7);

  const planeBottom = 100 - graphY - flightShiftY;

  // Measure the real board / svg / plane size so the plane is placed from the
  // actual end point of the red line (same result on every screen size).
  const boardRef = useRef(null);
  const svgRef = useRef(null);
  const planeRef = useRef(null);
  const [geom, setGeom] = useState(null);

  useLayoutEffect(() => {
    const measure = () => {
      const b = boardRef.current;
      const sv = svgRef.current;
      const pl = planeRef.current;
      if (!b || !sv) return;
      const br = b.getBoundingClientRect();
      const sr = sv.getBoundingClientRect();
      const next = {
        bw: br.width,
        sl: sr.left - br.left,
        st: sr.top - br.top,
        sw: sr.width,
        sh: sr.height,
        pw: pl ? pl.offsetWidth : 0,
        ph: pl ? pl.offsetHeight : 0,
      };
      setGeom((g) =>
        g && Object.keys(next).every((k) => Math.abs(next[k] - g[k]) < 0.5)
          ? g
          : next,
      );
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    const ro =
      typeof ResizeObserver !== "undefined" && boardRef.current
        ? new ResizeObserver(measure)
        : null;
    if (ro) ro.observe(boardRef.current);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
      if (ro) ro.disconnect();
    };
  }, [flying, isMobile]);

  // Plane box so that its tail sits on the end of the red line.
  const PLANE_TAIL_X = 0.18; // fraction of plane width left of the line end
  const PLANE_TAIL_Y = -0.1; // fraction of plane height below the line end
  let planePos = null;
  if (flying && geom && geom.pw && geom.sw) {
    const endX = geom.sl + ((graphX + flightShiftX) / 100) * geom.sw;
    const endY = geom.st + ((graphY + flightShiftY) / 100) * geom.sh;
    const left = Math.min(endX - PLANE_TAIL_X * geom.pw, geom.bw - geom.pw - 4);
    const top = endY + PLANE_TAIL_Y * geom.ph - geom.ph;
    planePos = { left: `${left}px`, top: `${top}px`, bottom: "auto" };
  }

  const planeRotation = -10 + 3 * Math.cos(arcPhase) * floatBlend;

  const progress = flying ? flightProgress * 100 : 0;

  const [recentValues, setRecentValues] = useState([]);
const API_URL = `${process.env.REACT_APP_API_URL_AVIATOR_NODE}/round-history`;

useEffect(() => {
  const fetchRoundHistory = async () => {
    try {
      const res = await fetch(API_URL, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });

      if (!res.ok) throw new Error("API request failed");

      const result = await res.json();

      if (result.success && Array.isArray(result.data)) {
        setRecentValues(
          result.data.map((item) => Number(item.crashPoint))
        );
      }
    } catch (error) {
      console.error("Round History Error:", error);
    }
  };

  fetchRoundHistory();

  const interval = setInterval(fetchRoundHistory, 5000);

  return () => clearInterval(interval);
}, []);

const recent =
  Array.isArray(game?.recent) && game.recent.length
    ? game.recent
    : recentValues;

  // const recent =
  //   Array.isArray(game?.recent) && game.recent.length
  //     ? game.recent
  //     : [
  //         3.48, 2.06, 4.09, 4.27, 1.46, 1.26, 1.14, 1.04, 2.92, 7.39, 1.08,
  //         1.09, 6.76, 5.17, 1.55, 8.27, 6.4, 1.31,
  //       ];

  const place = async (
    amount,
    betType,
    slot = 1,
    requestedAutoCashOut = null,
  ) => {
    const isAuto = betType === "AUTO";

    /*
     * AUTO preference is persistent on the backend.
     * If a current-round bet already exists, do not place a duplicate,
     * but AUTO mode can still be saved for future rounds by the backend.
     */
    if (!isAuto && autoActive) {
      const message =
        "Auto Bet is ON. Turn Auto Bet off to place a manual bet.";
      setError(message);
      showError(message);
      return;
    }

    if (!isAuto && (myBet1 || myBet2)) {
      const message = "Bet already placed. Waiting for next round.";

      setError(message);
      showError(message);
      return;
    }

    const safeBet = clampBet(amount);

    if (!betting || !game.roundId) {
      if (isAuto) {
        // There is no active betting round. Still allow the backend to
        // save the AUTO preference for the next round.
      } else {
        return;
      }
    }

    if (safeBet < MIN_BET || safeBet > MAX_BET) {
      const message = `Bet amount must be between ₹${MIN_BET} and ₹${MAX_BET.toLocaleString("en-IN")}.`;

      setError(message);
      showError(message);
      return;
    }

    setError("");

    let autoTarget = null;

    if (
      isAuto &&
      requestedAutoCashOut !== null &&
      requestedAutoCashOut !== undefined
    ) {
      autoTarget = Number(requestedAutoCashOut);

      if (
        !Number.isFinite(autoTarget) ||
        autoTarget < AUTO_CASHOUT_MIN ||
        autoTarget > AUTO_CASHOUT_MAX
      ) {
        const message = `Auto Cash Out must be between ${AUTO_CASHOUT_MIN}x and ${AUTO_CASHOUT_MAX}x`;

        setError(message);
        showError(message);
        return;
      }

      autoTarget = Number(autoTarget.toFixed(2));
    }

    try {
      const r = await placeAviatorBet({
        amount: safeBet,
        roundId: game.roundId || null,
        betType,
        autoCashOut: isAuto ? autoTarget : null,
      });

      /*
       * Axios response:
       * { success, message, data: {...} }
       */
      const responseData = r?.data?.data ?? r?.data ?? {};

      /*
       * When AUTO is enabled outside a usable betting window,
       * backend only saves the preference:
       *
       * appliedToCurrentRound: false
       *
       * In that case DO NOT create a fake local bet and DO NOT
       * deduct wallet balance.
       */
      if (isAuto && responseData.appliedToCurrentRound === false) {
        showSuccess(
          responseData.message ||
            "Auto Bet enabled. It will start from the next round.",
        );
        return;
      }

      const placedBet = responseData.bet || responseData.userBet || null;

      const localBet = placedBet || {
        _id: responseData.betId || null,
        roundId: responseData.roundId || game.roundId,
        amount: safeBet,
        betAmount: safeBet,
        status: "PENDING",
        betType,
        autoCashOut: isAuto ? autoTarget : null,
      };

      if (slot === 2) {
        setMyBet2(localBet);
      } else {
        setMyBet1(localBet);
      }

      // Some responses do not include the bet _id (needed for Cash Out): fetch it.
      if (!localBet._id) {
        window.setTimeout(async () => {
          try {
            const res = await getCurrentRoundWithBets();
            const d = res?.data?.data ?? res?.data ?? {};
            const mine = d.userBet || d.myBet || d.currentUserBet || null;
            if (mine?._id) {
              if (slot === 2) setMyBet2(mine);
              else setMyBet1(mine);
            }
          } catch (e) {
            /* socket / next poll will provide it */
          }
        }, 500);
      }

      // Header polls the main wallet; here we only apply a wallet the API returned.
      const respWallet =
        responseData.wallet || r?.data?.wallet || r?.wallet || null;
      if (respWallet) updateWallet(respWallet);

      showSuccess(
        isAuto
          ? r?.message ||
              responseData.message ||
              `Auto Bet placed • ₹${money(safeBet)}`
          : r?.message ||
              responseData.message ||
              `Bet placed successfully • ₹${money(safeBet)}`,
      );
    } catch (e) {
      const message =
        e?.response?.data?.message ||
        e?.message ||
        "Unable to place Aviator bet";

      setError(message);
      showError(message);
    }
  };

  const cashout = async (slot = 1) => {
    const selectedBet = slot === 2 ? myBet2 : myBet1;

    if (!selectedBet?._id) return;

    lastAutoCashoutNotice.current = String(selectedBet._id);
    setError("");

    try {
      const r = await cashoutAviatorBet(selectedBet._id);

      // Different backend versions return the cash-out result at different levels
      // (data.bet, data.payout, result.payout, transaction.amount, etc.).
      // Read all of them before falling back to stake × multiplier.
      const root = r?.data ?? r ?? {};

      const candidates = [
        root?.bet,
        root?.data?.bet,
        root?.result,
        root?.data,
        root?.transaction,
        root?.cashout,
        root?.cashOut,
        root?.payout,
        root?.data?.payout,
        root,
      ].filter(Boolean);

      const findNumber = (keys) => {
        for (const obj of candidates) {
          if (!obj || typeof obj !== "object") continue;

          for (const key of keys) {
            const value = num(obj?.[key], NaN);

            if (Number.isFinite(value) && value > 0) return value;
          }
        }

        return 0;
      };

      const placedBet =
        candidates.find(
          (x) =>
            x &&
            typeof x === "object" &&
            (x._id || x.betId || x.amount || x.betAmount),
        ) || null;

      if (slot === 2)
        setMyBet2(placedBet || { ...(selectedBet || {}), cashedOut: true });
      else setMyBet1(placedBet || { ...(selectedBet || {}), cashedOut: true });

      const slotAmount = slot === 2 ? amount2 : amount1;

      const stake = num(
        selectedBet?.amount ?? selectedBet?.betAmount ?? selectedBet?.stake,
        num(
          placedBet?.amount ?? placedBet?.betAmount ?? placedBet?.stake,
          slotAmount,
        ),
      );

      const responseMultiplier =
        findNumber([
          "cashoutMultiplier",
          "exitMultiplier",
          "multiplier",
          "cashOutMultiplier",
        ]) ||
        num(
          placedBet?.cashoutMultiplier ??
            placedBet?.exitMultiplier ??
            placedBet?.multiplier,
          multiplier,
        );

      // Prefer total payout/win. If the API only sends profit, convert it to
      // total payout by adding the original stake. Never display a false $0.00
      // when a valid cash-out multiplier is available.
      let responseAmount = findNumber([
        "winAmount",
        "winningAmount",
        "payoutAmount",
        "payout",
        "returnAmount",
        "cashoutAmount",
        "cashOutAmount",
        "totalPayout",
        "totalWin",
        "win",
        "amountWon",
      ]);

      if (!responseAmount) {
        const profit = findNumber([
          "profit",
          "netProfit",
          "winningProfit",
          "cashoutProfit",
        ]);

        if (profit > 0 && stake > 0) responseAmount = stake + profit;
      }

      if (!responseAmount && stake > 0 && responseMultiplier > 0) {
        responseAmount = stake * responseMultiplier;
      }

      const respWallet = root?.wallet || root?.data?.wallet || null;
      if (respWallet) updateWallet(respWallet);

      // Shared timer: manual cash out ka message bhi baaki messages ke saath clash nahi karta
      showNotice(
        {
          type: "cashout",
          multiplier: responseMultiplier,
          amount: responseAmount,
        },
        3800,
      );

      // showSuccess(
      //   `You have cashed out! ${responseMultiplier.toFixed(2)}x • ₹${money(responseAmount)}`,
      // );
    } catch (e) {
      const message =
        e?.response?.data?.message || e?.message || "Unable to cash out";

      setError(message);

      showError(message);
    }
  };

  return (
    <div className="aviator-shell">
      <header className="aviator-header d-none">
        <button
          className="aviator-logo"
          type="button"
          onClick={() => navigate("/game")}
        >
          Aviator
        </button>

        {/* <div className="aviator-head-right" ref={menuRef}>
          <button className="aviator-sound-toggle" type="button"
            aria-label={soundOn ? "Mute game audio" : "Unmute game audio"}
            aria-pressed={soundOn} onClick={() => setSoundOn((value) => !value)}>
            {soundOn ? "♫" : "♪̸"}
          </button>
          <div className="aviator-balance">
            ₹{money(balance)} <small>INR</small>
          </div>
          <button
            className="aviator-menu"
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
          >
            ≡
          </button>

        {menuOpen && (

          <div className="aviator-menu-pop">

            <button onClick={() => navigate("/deposit")}>Deposit</button>

            <button onClick={() => navigate("/withdraw")}>Withdraw</button>

            <button onClick={() => navigate("/history")}>Bet History</button>

            <button onClick={() => navigate("/profile")}>

              {user?.fullName || "Profile"}

            </button>

            <button onClick={doLogout}>Logout</button>

          </div>

        )}
        </div> */}
      </header>

      <div className="aviator-content">
        <aside className="aviator-bets">
          <div className="aviator-tabs">
            {["All Bets", "Previous", "Top"].map((t) => (
              <button
                key={t}
                className={tab === t ? "active" : ""}
                onClick={() => setTab(t)}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="aviator-summary">
            <div className="aviator-summary-top">
              <div className="aviator-avatar-stack">
                <i>◉</i>

                <i>●</i>

                <i>◈</i>
              </div>

              <div className="aviator-summary-count">
                <b>
                  {visiblePlayers.length}/
                  {Math.max(
                    visiblePlayers.length,
                    num(game?.playersCount, visiblePlayers.length),
                  )}
                </b>

                <small>Bets</small>
              </div>

              <div className="aviator-summary-win">
                {money(totalWin)}

                <small>Total win INR</small>
              </div>
            </div>

            <div className="aviator-progress">
              <i
                style={{
                  width: `${Math.min(100, visiblePlayers.length ? 100 : 0)}%`,
                }}
              />
            </div>
          </div>

          <div className="aviator-columns">
            <span>Player</span>

            <span>Bet INR</span>

            <span>X</span>

            <span>Win INR</span>
          </div>

          <div className="aviator-player-scroll">
            {visiblePlayers.map((p, i) => (
              <div
                className={`aviator-player-row ${p.win ? "won" : ""}`}
                key={`${p.name}-${i}`}
              >
                <div className="aviator-player">
                  <i className={`avatar-${i % 5}`}>
                    {p.avatar || String.fromCharCode(65 + (i % 26))}
                  </i>

                  <b>{p.name}</b>
                </div>

                <span>{money(p.amount)}</span>

                <strong>
                  {p.multiplier ? `${p.multiplier.toFixed(2)}x` : ""}
                </strong>

                <em>{p.win ? money(p.win) : ""}</em>
              </div>
            ))}
          </div>

          <div className="aviator-bets-footer">
            <span>◉ Provably Fair Game</span>

            <span>Powered by SPRIBE</span>
          </div>
        </aside>

        <main className="aviator-main">
          <div className="aviator-recent">
            {recent.map((x, i) => (
              <span key={i} className={Number(x) >= 5 ? "hot" : ""}>
                {Number(x).toFixed(2)}x
              </span>
            ))}

            <button className="more" onClick={() => setShowHistory(true)}>
              •••
            </button>
          </div>

          {showHistory && (
            <div
              className="round-history-overlay"
              onClick={() => setShowHistory(false)}
            >
              <div
                className="round-history-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="round-history-header">
                  <h5>Round History</h5>

                  <button
                    className="round-history-close"
                    onClick={() => setShowHistory(false)}
                  >
                    ×
                  </button>
                </div>

                <div className="round-history-list">
                  {recent.map((x, i) => (
                    <span key={i} className={Number(x) >= 5 ? "hot" : ""}>
                      {Number(x).toFixed(2)}x
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          <section
            ref={boardRef}
            className={`aviator-board phase-${(boardPhase === "TAKEOFF" ? "betting" : boardPhase).toLowerCase()}`}
          >
            <img className="bgoverlay" src={bgoverlay} alt="overlay" />
            <div className="aviator-modebar">FUN MODE</div>

            <div className="aviator-grid" />

            <div className="aviator-rays aviator-r1" />

            <div className="aviator-rays aviator-r2" />

            <div className="aviator-rays aviator-r3" />

            {(boardPhase === "BETTING" ||
              boardPhase === "TAKEOFF" ||
              boardPhase === "WAITING") && (
              <div
                className="betting-partners-stage"
                aria-label="Official partners"
              >
                <div className="betting-partners-art">
                  <img
                    className="betting-partners-image"
                    src={aviatorPartner}
                    alt="UFC Aviator official partners"
                  />

                  <div
                    className="betting-countdown-line"
                    aria-hidden="true"
                    style={{
                      "--betting-progress": `${Math.max(0, Math.min(100, Number(game?.bettingProgress ?? 1) * 100))}%`,
                    }}
                  >
                    <i />
                  </div>
                </div>
              </div>
            )}

            {cashoutNotice && (
              <div
                className={`aviator-cashout-notice ${cashoutNotice.type === "crash" ? "is-crash" : ""}`}
                role="status"
                aria-live="polite"
              >
                <span className="cashout-star" aria-hidden="true">
                  {cashoutNotice.type === "crash" ? "!" : "★"}
                </span>

                <div className="cashout-copy">
                  <strong>
                    {cashoutNotice.type === "autoBet"
                      ? "Auto Bet placed!"
                      : cashoutNotice.type === "autoCashout"
                        ? "Auto Cash Out!"
                        : cashoutNotice.type === "crash"
                          ? "Round crashed!"
                          : "You have cashed out!"}
                  </strong>

                  {cashoutNotice.type === "autoBet" ? (
                    <span>Next round • ₹{money(cashoutNotice.amount)}</span>
                  ) : (
                    <span>{cashoutNotice.multiplier.toFixed(2)}x</span>
                  )}
                </div>

                <div className="cashout-win">
                  <small>
                    {cashoutNotice.type === "autoBet"
                      ? "ROUND"
                      : cashoutNotice.type === "crash"
                        ? "Lost INR"
                        : "Win INR"}
                  </small>

                  <b>
                    {cashoutNotice.type === "autoBet"
                      ? `#${cashoutNotice.roundId ?? "NEXT"}`
                      : `₹${money(cashoutNotice.amount)}`}
                  </b>
                </div>

                <button
                  type="button"
                  aria-label="Close cash out message"
                  onClick={() => setCashoutNotice(null)}
                >
                  ×
                </button>
              </div>
            )}

            <div className="aviator-multiplier">
              {boardPhase === "CRASHED" && <small>FLEW AWAY!</small>}

              <strong>
                {(boardPhase === "CRASHED"
                  ? num(game.crashShownValue, displayMultiplier)
                  : displayMultiplier
                ).toFixed(2)}
                x
              </strong>

              {/* {flying && <small>FLYING</small>} */}
            </div>

            <svg
              ref={svgRef}
              className="aviator-flight-svg"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <defs>
                <linearGradient
                  id="aviatorFlightFill"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0" stopColor="#f9003e" stopOpacity="0.65" />

                  <stop offset="1" stopColor="#f9003e" stopOpacity="0.65" />
                </linearGradient>

                <filter
                  id="aviatorFlightGlow"
                  x="-30%"
                  y="-30%"
                  width="160%"
                  height="160%"
                >
                  <feGaussianBlur stdDeviation="1.5" result="blur" />
                </filter>
              </defs>

              {flying && (
                <g transform={`translate(${flightShiftX}, ${flightShiftY})`}>
                  <path
                    className="aviator-flight-fill"
                    d={`${graphPath} L ${graphX} 96 L 4 96 Z`}
                    fill="url(#aviatorFlightFill)"
                  />

                  <path
                    className="aviator-flight-glow"
                    d={graphPath}
                    fill="none"
                  />

                  <path
                    className="aviator-flight-line"
                    d={graphPath}
                    fill="none"
                  />
                </g>
              )}
            </svg>

            {flying && (
              <div
                ref={planeRef}
                className={`aviator-plane-stage ${flying ? "flying" : "waiting"}`}
                style={{
                  ...(planePos || {
                    left: flying
                      ? `min(${planeX}%, calc(100% - var(--flight-plane-width) - 4px))`
                      : "4%",
                    bottom: flying
                      ? `calc((100% - 24px) * ${planeBottom / 100})`
                      : "4%",
                  }),

                  transform: `translate(0, 0) rotate(${flying ? planeRotation : -2}deg)`,
                  transition: "none",
                }}
              >
                <Plane />
              </div>
            )}

            <div className="aviator-board-stats">
              <div className="aviator-mini-avatars">
                <i>◉</i>

                <i>●</i>

                <i>◈</i>
              </div>

              <span>
                {Math.max(visiblePlayers.length, num(game?.playersCount, 155))}
              </span>
            </div>

            <div className="aviator-live-chip">● LIVE</div>
          </section>

          <div className="aviator_betbar">
            <BetCard
              amount={amount1}
              setAmount={setAmount1}
              onBet={(amount, type, autoCashOut) =>
                place(amount, type, 1, autoCashOut)
              }
              disabled={
                !betting ||
                hasRoundBet ||
                amount1 < MIN_BET ||
                amount1 > MAX_BET
              }
              locked={lockedBet1}
              active={activeBet1 && flying}
              multiplier={displayMultiplier}
              betAmount={myBet1?.amount}
              betAutoCashOut={myBet1?.autoCashOut}
              cashOutAutoEnabled={autoCashOut1}
              onCashout={() => cashout(1)}
              autoBetEnabled={autoBet1}
              manualBlocked={autoActive && !autoBet1}
              setAutoBetEnabled={handleAutoBetToggle(1)}
              autoCashOutEnabled={autoCashOut1}
              setAutoCashOutEnabled={setAutoCashOut1}
              autoCashOut={autoCashOutMultiplier1}
              setAutoCashOut={setAutoCashOutMultiplier1}
            />

            <BetCard
              amount={amount2}
              setAmount={setAmount2}
              onBet={(amount, type, autoCashOut) =>
                place(amount, type, 2, autoCashOut)
              }
              disabled={
                !betting ||
                hasRoundBet ||
                amount2 < MIN_BET ||
                amount2 > MAX_BET
              }
              locked={lockedBet2}
              active={activeBet2 && flying}
              multiplier={displayMultiplier}
              betAmount={myBet2?.amount}
              betAutoCashOut={myBet2?.autoCashOut}
              cashOutAutoEnabled={autoCashOut2}
              onCashout={() => cashout(2)}
              autoBetEnabled={autoBet2}
              manualBlocked={autoActive && !autoBet2}
              setAutoBetEnabled={handleAutoBetToggle(2)}
              autoCashOutEnabled={autoCashOut2}
              setAutoCashOutEnabled={setAutoCashOut2}
              autoCashOut={autoCashOutMultiplier2}
              setAutoCashOut={setAutoCashOutMultiplier2}
            />
          </div>
        </main>
      </div>
    </div>
  );
}
